import { gql } from "@apollo/client";
import  { useMemo } from "react";
import useStdQuery from "../hooks/useStdQuery.js";

import PublicAppVars from 'utils/PublicAppVars.js';
import { Trip } from "server/api-types/WSRideHistoryFactory.js";
import { getDefaultFilterDates } from "utils/TripHistory.js";
import { useTransitAccountIdContext } from "context/TransitAccountIdContext.js";


const travelPresenceEventsFragment = `
	objectType
	travelPresenceId
	transactionDateTime
	type
	typeDescription
	locationCode
	operator
	operatorDescription
	status
	statusDescription
	stopPoint
	stopPointDescription
	locationDescription
	routeNumber
	generatedCause
	generatedCauseDescription
	travelPresenceIndicator
	processingStatus
	correctionDateTime
	correctionStatus
	zone
	transactionStatusDescription
`;

const rideHistoryFragment = `
(
	$subsystemAccountRef: ID!
	$startDateTime: String
	$endDateTime: String
	$specificDate: String
	$sortBy: String
	$offset: Int
	$limit: Int
	$dateFilterType: String
	$type: String
) {
	TransitAccountRoute {
		getRidesHistory (
			subsystemAccountRef: $subsystemAccountRef
			startDateTime: $startDateTime
			endDateTime: $endDateTime
			specificDate: $specificDate
			offset: $offset
			limit: $limit
			dateFilterType: $dateFilterType
			type: $type
			sortBy: $sortBy
		) {
			totalCount
			lineItems {
				__typename
				subsystemAccountReference
				rideId
				startDateTime
				endDateTime
				type
				travelMode
				travelModeDescription
				startLocationDescription
				endLocationDescription
				restrictedTravelCorrectionActions {
					key
					value
				}
				...on WSTripRideHistory {
					travelMode
					totalFare
					tripStatus
					riderClass
					riderClassDescription
					fareDescription
					isCorrectable
					isVoidable
					isFareAdjustable
					fareNotAdjustableReasons
					fareAdjustmentAmount
					tripCorrectionStatus
					tripCorrectionDateTime
					travelPresenceEvents {
						...on WSRideHistoryTransitTravelPresence {
							${travelPresenceEventsFragment}
						}
					}
					tripPayments {
						productDescription
						purseDescription
					}
				}

				...on WSInspectionTapRideHistory {
					travelPresenceEvents {
						...on WSRideHistoryTransitTravelPresence {
							${travelPresenceEventsFragment}
						}
					}
				}

				...on WSRejectedTapRideHistory {
					travelPresenceEvents {
						...on WSRideHistoryTransitTravelPresence {
							${travelPresenceEventsFragment}
						}
					}
				}
			}
		}
	}
}
`;

// This query is purely a name change so we can delineate between the differet Ride History calls
const GET_RIDES_HISTORY_MISSED_TAPS = gql`
	query transitAccountMissedTapsGET
		${rideHistoryFragment}
`;

export const GET_RIDES_HISTORY = gql`
	query transitAccountRidesHistoryGET
		${rideHistoryFragment}
`;

export const useRideHistory = ({
	offset = 0,
	filtersApplied = null, // Keep the filters null here to allow parent to add them before calling this hook. This is an expensive query and we want to minimize the calls
	subsystemAccountRef = null,
	type = null,
	limit = PublicAppVars.TRIPS_HISTORY_PAGE_SIZE,
}) => {
	const variables = useMemo(() => Object.freeze({
		offset,
		...filtersApplied,
		subsystemAccountRef,
		limit,
		type,
	}),[ filtersApplied, limit, offset, subsystemAccountRef, type ]);

	const queryResult = useStdQuery(GET_RIDES_HISTORY, {
		variables,
		skip: !subsystemAccountRef || !filtersApplied,
	 });

	const { lineItems, totalCount } = useMemo(() =>
		queryResult?.data?.TransitAccountRoute?.getRidesHistory ?? {}
	,[ queryResult ]);

	return {
		lineItems,
		totalCount,
		...queryResult,
	};
};


export const useMissedTapsHistory = () => {
	const subsystemAccountRef = useTransitAccountIdContext();

	const variables = useMemo(() => Object.freeze({
		offset: 0,
		...getDefaultFilterDates({ isAlerts: true }),
		subsystemAccountRef,
		limit: PublicAppVars.TRIPS_HISTORY_PAGE_SIZE * 3,
		type: Trip,
	}),[  subsystemAccountRef ]);


	const queryResult = useStdQuery(GET_RIDES_HISTORY_MISSED_TAPS, {
		variables,
		skip: !variables.subsystemAccountRef,
	 });

	const { lineItems, totalCount } = useMemo(() =>
		queryResult?.data?.TransitAccountRoute?.getRidesHistory ?? {}
	,[ queryResult ]);

	return {
		lineItems,
		totalCount,
		...queryResult,
	};
};
